import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const EnergyTrackerDashboard = React.lazy(
  () => import("./EnergyTrackerDashboard")
);

const PersonsOfInterestPage = React.lazy(
  () => import("./PersonsOfInterestPage")
);

const NotificationSettings = React.lazy(
  () => import("./NotificationSettings")
);

const energyTrackerModuleConfig: ModuleConfig = {
  id: "energy-tracker",
  name: "Energy Tracker",
  description:
    "National fuel supply chain tracker with interactive OpenStreetMap view, REA value flows, and person of interest calendar",
  version: "1.0.0",
  enabled: true,
  category: "research",
  order: 50,
  icon: "\u26FD",
  color: "red",

  routes: [
    {
      path: "/energy-tracker",
      component: EnergyTrackerDashboard,
      exact: true,
      permissionLevel: "view",
      useLayout: true,
      layoutOptions: { noContainer: true },
    },
    {
      path: "/energy-tracker/persons-of-interest",
      component: PersonsOfInterestPage,
      exact: true,
      permissionLevel: "view",
      useLayout: true,
      layoutOptions: { maxW: "container.xl" },
    },
    {
      path: "/energy-tracker/notifications",
      component: NotificationSettings,
      exact: true,
      permissionLevel: "view",
      useLayout: true,
      layoutOptions: { maxW: "container.xl" },
    },
  ],

  navigation: [
    {
      label: "Energy Map",
      path: "/energy-tracker",
      icon: "\u26FD",
      permissionLevel: "view",
    },
    {
      label: "Persons of Interest",
      path: "/energy-tracker/persons-of-interest",
      icon: "\uD83D\uDC64",
      permissionLevel: "view",
    },
    {
      label: "Notifications",
      path: "/energy-tracker/notifications",
      icon: "\uD83D\uDD14",
      permissionLevel: "view",
    },
  ],

  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  quickActions: [
    {
      label: "View Energy Map",
      path: "/energy-tracker",
      icon: "\u26FD",
      description: "Open the national fuel supply tracker map",
    },
  ],

  changelog: [
    {
      version: "1.0.0",
      releaseDate: "2026-03-27",
      title: "Initial Release - Interactive Fuel Map",
      features: [
        "Interactive OpenStreetMap with fuel station markers",
        "Fuel type overlay toggles (Diesel, ULP, E10, LPG)",
        "Stress status color coding (Normal, Low, Critical, Dry)",
        "Full-screen map mode",
        "Trade route visualization with volume-based line thickness",
        "Station detail drawer with fuel levels and capacity",
        "Overpass API seeding for Queensland stations",
      ],
    },
  ],

  roadmap: [
    {
      title: "REA Supply Chain Flows",
      description:
        "Supply agent/event/resource tracking using ValueFlows concepts",
      targetVersion: "2.0.0",
      priority: "high" as const,
      status: "planned" as const,
      features: ["Supply agents", "Supply events", "Resource tracking"],
    },
    {
      title: "Person of Interest Calendar",
      description:
        "Track key decision makers with FullCalendar integration and news cron",
      targetVersion: "3.0.0",
      priority: "high" as const,
      status: "planned" as const,
      features: ["POI profiles", "Calendar events", "News auto-classification"],
    },
    {
      title: "Timeline Slider",
      description: "Scrub through historical fuel supply states on the map",
      targetVersion: "3.0.0",
      priority: "medium" as const,
      status: "planned" as const,
      features: ["Time slider", "Historical snapshots", "Animated playback"],
    },
    {
      title: "Shipment Tracking",
      description: "MarineTraffic AIS data for fuel tanker positions and ETAs",
      targetVersion: "2.0.0",
      priority: "high" as const,
      status: "planned" as const,
      features: ["AIS vessel tracking", "Tanker ETA overlay", "Cargo type filter"],
    },
    {
      title: "Australia-Wide Expansion",
      description: "Expand from QLD to all states (~8000 stations)",
      targetVersion: "4.0.0",
      priority: "medium" as const,
      status: "planned" as const,
      features: ["All state Overpass seeding", "State aggregation", "National overview map"],
    },
  ],
};

export default energyTrackerModuleConfig;
